// Conversations this tab marked read or unread whose job has not broadcast yet. The inbox
// badges are counted on the replica, which may not have the change, so every count applies
// these marks until the job's broadcast settles them. Expired marks are dropped.
export const PENDING_MARK_TTL_MS = 60 * 1000

const pendingReads = new Map()
const pendingUnreads = new Map()

const prune = (marks, now) => {
  marks.forEach((mark, contactId) => {
    if (now - mark.at > PENDING_MARK_TTL_MS) {
      marks.delete(contactId)
    }
  })
}

const contactCount = (row, contactId) => {
  const contactUnreadKey = `unread_contact_${contactId}`

  return Number(row?.[contactUnreadKey] || 0)
}

// Every unread of these conversations in `inboxIds` is being cleared
export function addPendingReads (contactIds, inboxIds, now = Date.now()) {
  contactIds.forEach(contactId => {
    pendingReads.set(contactId, { inboxIds, at: now })
    pendingUnreads.delete(contactId)
  })
}

// One communication of each conversation is being flipped to unread, counted in `inboxId`
export function addPendingUnreads (marks, inboxIds, now = Date.now()) {
  marks.forEach(({ contactId, inboxId }) => {
    pendingUnreads.set(contactId, { inboxIds, inboxId, at: now })
    pendingReads.delete(contactId)
  })
}

export function settlePendingRead (contactId) {
  pendingReads.delete(contactId)
}

export function settlePendingUnread (contactId) {
  pendingUnreads.delete(contactId)
}

export function pendingContactIds (now = Date.now()) {
  prune(pendingReads, now)
  prune(pendingUnreads, now)

  return [...new Set([...pendingReads.keys(), ...pendingUnreads.keys()])]
}

// The badge count of each inbox in an unread count response, with the pending marks applied.
// The response must carry the unread_contact_<id> column of every pending conversation.
export function applyPendingMarks (rows, inboxIds) {
  const rowOf = inboxId => (rows || []).find(row => row.ring_group_id === inboxId)
  const counts = new Map(inboxIds.map(inboxId => [inboxId, Number(rowOf(inboxId)?.unread_count || 0)]))

  pendingReads.forEach(({ inboxIds: scope }, contactId) => {
    scope.filter(inboxId => counts.has(inboxId)).forEach(inboxId => {
      counts.set(inboxId, counts.get(inboxId) - contactCount(rowOf(inboxId), contactId))
    })
  })

  // the replica shows none of the conversation's unreads yet, so the flip is not in the count
  pendingUnreads.forEach(({ inboxIds: scope, inboxId }, contactId) => {
    const counted = scope.some(scopeInboxId => contactCount(rowOf(scopeInboxId), contactId) > 0)

    if (!counted && counts.has(inboxId)) {
      counts.set(inboxId, counts.get(inboxId) + 1)
    }
  })

  return new Map([...counts].map(([inboxId, count]) => [inboxId, Math.max(count, 0)]))
}

export function resetPendingMarks () {
  pendingReads.clear()
  pendingUnreads.clear()
}
